import { useEffect, useState } from "react";

import { booksInSeries, SERIES } from "../config/site";
import type { CharacterPage } from "../data/character-pages";
import { sectionFromFolder } from "../data/compendium-sections";
import { loadCompendium } from "../data/load-compendium";

async function characterName(seriesId: string, id: string): Promise<string | undefined> {
  const books = await Promise.all(booksInSeries(seriesId).map((book) => (
    fetch(`/book-data/${book.id}/character-pages.json`)
      .then((response) => response.json() as Promise<{ characters: CharacterPage[] }>)
      .catch(() => ({ characters: [] }))
  )));
  return books.flatMap((book) => book.characters)
    .find((character) => character.id === id)?.displayName;
}

async function entryName(
  seriesId: string,
  id: string,
  folder: string,
): Promise<string | undefined> {
  const kind = sectionFromFolder(folder)?.kind;
  const books = await Promise.all(booksInSeries(seriesId).map((book) => loadCompendium(book.id)));
  const entries = books.flat().filter((entry) => entry.id === id);
  return (entries.find((entry) => entry.kind === kind) ?? entries[0])?.name;
}

/** The name on a character's or compendium entry's page, for the end of the path. */
export function useAppNavPageName(pathname: string): string | null {
  // Kept with its address, so a page never shows the last page's name while its own loads.
  const [named, setNamed] = useState<{ pathname: string; name: string } | null>(null);
  const [, seriesSlug, section, id] = pathname.split("/");
  useEffect(() => {
    const series = SERIES.find((item) => item.slug === seriesSlug);
    if (series === undefined || section === undefined || id === undefined) {
      return;
    }
    const lookup = section === "characters"
      ? characterName(series.id, id)
      : sectionFromFolder(section) === undefined
        ? Promise.resolve(undefined)
        : entryName(series.id, id, section);
    let current = true;
    void lookup.then((found) => {
      if (current && found !== undefined) {
        setNamed({ pathname, name: found });
      }
    });
    return () => {
      current = false;
    };
  }, [id, pathname, section, seriesSlug]);
  return named?.pathname === pathname ? named.name : null;
}
